import React, { useState, useContext, useEffect } from "react";
import moengage from "@moengage/web-sdk";
import { useNavigate } from "react-router-dom";
import Button from "../../components/core/EPL_Button_Think";
import FloatingInput from "../../components/base/FloatingInput";
import Title from "../../components/core/Title";
import ShimmerPlaceholder from "../../components/placeholders/ShimmerPlaceholder";
import { AfterLoginLayoutWrapper } from "../_styles/EPL_AfterLoginLayoutWrapper_think";
import { DocUploadWrapper } from "./_styles/DocUploadWrapper";
import { GetCustomerDetailsContext, validateInputExistenece } from "../../App";
import { getRouteFromLoanStageService } from "../../shared/services/getRouteFromLoanStageService";
import sendRequest from "../../utils/sendRequest";
import ShimmerPage from "../other-pages/ShimmerPage";
import { GET_DOCS, UPLOAD_DOC } from "../../Config";
import Pdf_icon from "../../assets/svg/EPL_think_svg/Pdf_icon.svg";
import { encryptedData } from "../../utils";
import { isStageLogin } from "../../shared/validations/StageLogin";
moengage.initialize({ app_id: "L9SUGRVWTLEJA9UWH2ZEAMHL", cluster: "dc_3" });

const DocUpload = () => {
    const navigate = useNavigate();
    const [customerDetails, setCustomerDetails] = useContext(GetCustomerDetailsContext);
    const [showShimmer, setShowShimmer] = useState(false);
    const [docProofOptions, setDocProofOptions] = useState([]);
    const [selectedDoc1, setSelectedDoc1] = useState(null);
    const [selectedDoc2, setSelectedDoc2] = useState(null);
    const [fileErrors, setFileErrors] = useState({});
    const [uploadedFiles, setUploadedFiles] = useState({});
    const [uploadedFilePasswords, setUploadedFilePasswords] = useState({});
    const [salarySlipFiles, setSalarySlipFiles] = useState({});
    const [salarySlipFileErrors, setSalarySlipFileErrors] = useState({});
    const [salarySlipPasswords, setSalarySlipPasswords] = useState({});
    const [docError, setDocError] = useState("");

    const MAX_FILE_SIZE = 10 * 1024 * 1024;  // 10MB

    useEffect(() => {
        if (validateInputExistenece(customerDetails) && validateInputExistenece(customerDetails["stage"])) {
            var route = getRouteFromLoanStageService({
                source: customerDetails.source,
                stage: customerDetails.stage,
            });
            navigate(`${route}`, {
                replace: true,
                state: {
                    customerDetails: customerDetails,
                },
            });
        }
    }, [customerDetails]);

    useEffect(() => {
        isStageLogin(    
            customerDetails?.Data?.uuid,
            customerDetails?.Data?.mobile,
            customerDetails?.source,
        );
        setTimeout(() => {
            moengage.track_event("doc_upload_onload", {
                application_form_number: sessionStorage.getItem(
                  "application_form_no"
                ),
                "journey name": customerDetails.source
            });
        }, 1000);   

        const getAllDocs = async () => {
            try {
                setShowShimmer(true);
                const formData = new FormData();
                const requestData = {};
                formData.append("data", encryptedData(requestData));
                const response = await sendRequest("/" + customerDetails.source + GET_DOCS, formData);

                if (response.response.Success) {
                    setShowShimmer(false);
                    const docData = response.response.data.map(item => ({
                        document_type: item.document_type,
                        document_name: item.document_name,
                        document_extension: item.document_extension,
                    }));
                    setDocProofOptions(docData);
                }
            } catch (error) {
                if (error === "Unauthorised") {
                    var route = getRouteFromLoanStageService({
                        source: customerDetails.source,
                        stage: "",
                    });
                    navigate(`${route}`, {
                        replace: true,
                    });
                } else {
                    var route = getRouteFromLoanStageService({
                        source: customerDetails.source,
                        stage: "error",
                    });
                    navigate(`${route}`, {
                        replace: true,
                    });
                }
            } finally {
                setShowShimmer(false);
            }
        };

        if (customerDetails?.Data?.employment_type === "self_employed") {
            getAllDocs()
        }
    }, []);

    const validateFile = (file, docType) => {
        const validExtensions = docProofOptions.find(doc => doc.document_type === docType)?.document_extension;

        if (!validExtensions) return "No valid extensions found for this document.";

        const validExtensionsString = validExtensions.join(",").toLowerCase();

        if (file?.size > MAX_FILE_SIZE) {
            return "File size should not exceed 10MB.";
        }

        const fileExtension = file?.name.split('.').pop().toLowerCase();

        if (!validExtensionsString.split(",").includes(fileExtension)) {
            return `Invalid file type. Accepted formats are: ${validExtensions.join(", ")}.`;
        }
        return null;
    };

    const validateSalarySlipFile = (file) => {
        if (file?.size > MAX_FILE_SIZE) {
            return "File size should not exceed 10MB.";
        }
        return null;
    };

    const handleFileChange = (e, docType) => {
        const file = e.target.files[0];
        const error = validateFile(file, docType);
        setFileErrors((prevErrors) => ({ ...prevErrors, [docType]: error }));

        if (!error) {
            setUploadedFiles((prevFiles) => ({ ...prevFiles, [docType]: file }));
        } else {
            setUploadedFiles((prevFiles) => ({ ...prevFiles, [docType]: null }));
        }
    };

    const handleSalarySlipFileChange = (e, docType) => {
        const file = e.target.files[0];
        const error = validateSalarySlipFile(file, docType);
        setSalarySlipFileErrors((prevErrors) => ({ ...prevErrors, [docType]: error }));

        if (!error) {
            setSalarySlipFiles((prevFiles) => ({ ...prevFiles, [docType]: file }));
        }
    };

    const handleSalarySlipPasswordChange = (e, docType) => {
        setSalarySlipPasswords((prevPasswords) => ({
            ...prevPasswords,
            [docType]: e.target.value,
        }));
    };

    const clearAllSelections = () => {
        setSelectedDoc1(null);
        setSelectedDoc2(null);
        setUploadedFiles({});
        setUploadedFilePasswords({});
        setFileErrors({});
    };

    const handleOnSubmit = async () => {
        const allFilesValid = Object.keys(uploadedFiles).every((docType) => uploadedFiles[docType]);

        if (!allFilesValid) {
            setDocError("Please upload a file for each selected document.");
        } else {
            setDocError("");
        }

        try {
            setShowShimmer(true);
            const formData = new FormData();
            const documentData = [];

            // Function to format the document name (lowercase, spaces to underscores)
            const formatDocName = (name) => name.toLowerCase().replace(/\s+/g, '_');

            if (selectedDoc1) {
                const formattedDoc1 = formatDocName(selectedDoc1);
                documentData.push({
                    document_type: formattedDoc1,
                    files: [
                        {
                            filename: formattedDoc1,
                            document_password: uploadedFilePasswords[selectedDoc1] || "",
                        }
                    ]
                });
            }
            if (selectedDoc2) {
                const formattedDoc2 = formatDocName(selectedDoc2);
                documentData.push({
                    document_type: formattedDoc2,
                    files: [
                        {
                            filename: formattedDoc2,
                            document_password: uploadedFilePasswords[selectedDoc2] || "",
                        }
                    ]
                });
            }

            const payload = { data: documentData };
            const encryptedPayload = encryptedData(payload);
            formData.append("data", encryptedPayload);

            Object.keys(uploadedFiles).forEach((docType) => {
                const file = uploadedFiles[docType];
                // Format file name to lowercase with underscores
                const formattedFileName = formatDocName(docType);
                formData.append(formattedFileName, file);
            });

            const response = await sendRequest("/" + customerDetails.source + UPLOAD_DOC, formData);
            if (response.response.Success) {
                setCustomerDetails({ ...customerDetails, stage: response.response.STAGE });
            }
        } catch (error) {
            if (error === "Unauthorised") {
                var route = getRouteFromLoanStageService({
                    source: customerDetails.source,
                    stage: "",
                });
                navigate(`${route}`, {
                    replace: true,
                });
            } else {
                var route = getRouteFromLoanStageService({
                    source: customerDetails.source,
                    stage: "error",
                });
                navigate(`${route}`, {
                    replace: true,
                });
            }
        } finally {
            setShowShimmer(false);
        }
    };

    const handleSalarySlipSubmit = async () => {
        const allFilesValid = Object.keys(salarySlipFiles).every((docType) => salarySlipFiles[docType]);

        if (!allFilesValid) {
            setDocError("Please upload a file for each salary slip.");
        } else {
            setDocError("")
        }

        try {
            setShowShimmer(true);
            const formData = new FormData();

            // Filter out document types without files
            const salarySlipData = ["salary-slip1", "salary-slip2", "salary-slip3"]
                .filter((docType) => salarySlipFiles[docType])  // Only include document types with files
                .map((docType) => ({
                    document_type: docType,
                    files: [
                        {
                            filename: docType,
                            document_password: salarySlipPasswords[docType] || "",
                        }
                    ]
                }));

            const payload = { data: salarySlipData };
            const encryptedPayload = encryptedData(payload);
            formData.append("data", encryptedPayload);

            // Append files to formData
            Object.keys(salarySlipFiles).forEach((docType) => {
                const file = salarySlipFiles[docType];
                if (file) {  // Only append the file if it exists
                    formData.append(docType, file);
                }
            });

            const response = await sendRequest("/" + customerDetails.source + UPLOAD_DOC, formData);
            if (response.response.Success) {
                setCustomerDetails({ ...customerDetails, stage: response.response.STAGE });
            }
            moengage.track_event("doc_upload_proceed", {
                document_uploaded_successfully: response.response.Success? "Yes": "No",
                application_form_number: sessionStorage.getItem(
                  "application_form_no"
                ),
                "journey name": customerDetails.source,
            });
        } catch (error) {
            if (error === "Unauthorised") {
                var route = getRouteFromLoanStageService({
                    source: customerDetails.source,
                    stage: "",
                });
                navigate(`${route}`, {
                    replace: true,
                });
            } else {
                var route = getRouteFromLoanStageService({
                    source: customerDetails.source,
                    stage: "error",
                });
                navigate(`${route}`, {
                    replace: true,
                });
            }
        } finally {
            setShowShimmer(false);
        }
    };

    const handleDocSelect1 = (docType) => {
        if (docType === selectedDoc2) {
            setFileErrors(prevErrors => ({
                ...prevErrors,
                docError: "You cannot select the same document for both fields."
            }));
        } else {
            setFileErrors(prevErrors => ({ ...prevErrors, docError: "" }));
            setSelectedDoc1(docType);
            setUploadedFiles(prevFiles => ({ ...prevFiles, [docType]: null }));
        }
    };

    const handleDocSelect2 = (docType) => {
        if (docType === selectedDoc1) {
            setFileErrors(prevErrors => ({
                ...prevErrors,
                docError: "You cannot select the same document for both fields."
            }));
        } else {
            setFileErrors(prevErrors => ({ ...prevErrors, docError: "" }));
            setSelectedDoc2(docType);
            setUploadedFiles(prevFiles => ({ ...prevFiles, [docType]: null }));
        }
    };

    const clearSelection1 = () => {
        setSelectedDoc1(null);
        delete uploadedFiles[selectedDoc1];
        delete uploadedFilePasswords[selectedDoc1];
        setFileErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[selectedDoc1];
            return newErrors;
        });
    };

    const clearSelection2 = () => {
        setSelectedDoc2(null);
        delete uploadedFiles[selectedDoc2];
        delete uploadedFilePasswords[selectedDoc2];
        setFileErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[selectedDoc2];
            return newErrors;
        });
    };

    const showDropdown = () => {
        document.getElementById("myDropdown").classList.toggle("show");
    };

    const showDropdownDOC = () => {
        document.getElementById("myDropdownDOC").classList.toggle("show");
    };

    const handleUploadedFilePasswordChange = (e, docType) => {
        setUploadedFilePasswords((prevPasswords) => ({
            ...prevPasswords,
            [docType]: e.target.value,
        }));
    };

    return (
        <>
            {showShimmer ? (
                <ShimmerPage />
            ) : (
                <AfterLoginLayoutWrapper>
                    <DocUploadWrapper>
                        <div className="additional-form-title">
                            <Title text="Document Upload" visible={!showShimmer} clickable={false} />
                        </div>
                        {
                            customerDetails?.Data?.employment_type === "self_employed" ? (
                                <form className="additional-form">
                                    <ShimmerPlaceholder visible={showShimmer} />
                                    <FloatingInput
                                        visible={!showShimmer}
                                        value={docProofOptions.find(doc => doc.document_type === selectedDoc1)?.document_name || ""}
                                        type="text"
                                        label="Select Document (1)"
                                        options={docProofOptions.map((option) => ({
                                            label: option.document_name,
                                            value: option.document_type,
                                            func: () => {
                                                handleDocSelect1(option.document_type)
                                                showDropdown()
                                            }
                                        }))}
                                    />

                                    {selectedDoc1 && (
                                        <>
                                            <div key={selectedDoc1} className="file-inputs">
                                                <label htmlFor={selectedDoc1}>{`Upload ${docProofOptions.find(doc => doc.document_type === selectedDoc1)?.document_name}`}</label>
                                                <div className="file-input-box">
                                                    <input
                                                        type="file"
                                                        id={selectedDoc1}
                                                        onChange={(e) => handleFileChange(e, selectedDoc1)}
                                                        accept={docProofOptions.find(doc => doc.document_type === selectedDoc1)?.document_extension.map(ext => `.${ext}`).join(",")}
                                                    />
                                                    <span className="clear-icon" onClick={() => clearSelection1()}>&times;</span>
                                                </div>
                                                <div className="error">{fileErrors[selectedDoc1]}</div>
                                            </div>
                                            <FloatingInput
                                                type="text"
                                                label={`Enter Password (Mandatory for Password Protected Documents)`}
                                                value={uploadedFilePasswords[selectedDoc1] || ""}
                                                on_change={(e) => handleUploadedFilePasswordChange(e, selectedDoc1)}
                                                class_name="docpass-input"
                                            />
                                        </>
                                    )}

                                    <div style={{ height: "20px" }}></div>

                                    <FloatingInput
                                        visible={!showShimmer}
                                        value={docProofOptions.find(doc => doc.document_type === selectedDoc2)?.document_name || ""}
                                        type="text"
                                        label="Select Document (2) (Optional)"
                                        optionsDOC={docProofOptions.map((option) => ({
                                            label: option.document_name,
                                            value: option.document_type,
                                            func: () => {
                                                handleDocSelect2(option.document_type)
                                                showDropdownDOC()
                                            }
                                        }))}
                                    />

                                    {selectedDoc2 && (
                                        <>
                                            <div key={selectedDoc2} className="file-inputs">
                                                <label htmlFor={selectedDoc2}>{`Upload ${docProofOptions.find(doc => doc.document_type === selectedDoc2)?.document_name}`}</label>
                                                <div className="file-input-box">
                                                    <input
                                                        type="file"
                                                        id={selectedDoc2}
                                                        onChange={(e) => handleFileChange(e, selectedDoc2)}
                                                        accept={docProofOptions.find(doc => doc.document_type === selectedDoc2)?.document_extension.map(ext => `.${ext}`).join(",")}
                                                    />
                                                    <span className="clear-icon" onClick={() => clearSelection2()}>&times;</span>
                                                </div>
                                                <div className="error">{fileErrors[selectedDoc2]}</div>
                                            </div>
                                            <FloatingInput
                                                type="text"
                                                label={`Enter Password (Mandatory for Password Protected Documents)`}
                                                value={uploadedFilePasswords[selectedDoc2] || ""}
                                                on_change={(e) => handleUploadedFilePasswordChange(e, selectedDoc2)}
                                                class_name="docpass-input"
                                            />
                                        </>
                                    )}

                                    <div className="bottomDiv">
                                        <Button text="Clear All" type="button" onClick={clearAllSelections} />
                                        <Button
                                            text="Proceed"
                                            type="submit"
                                            disabled={
                                                (!selectedDoc1 && !selectedDoc2) ||
                                                (selectedDoc1 && !uploadedFiles[selectedDoc1]) ||
                                                (selectedDoc2 && !uploadedFiles[selectedDoc2]) ||
                                                Object.values(fileErrors).some(error => error) ||
                                                docProofOptions.length === 0
                                            }
                                            onClick={handleOnSubmit}
                                        />
                                    </div>
                                    <div className="doc-note">
                                        <p><strong>Please ensure that your business is active for minimum for 2 years.</strong></p>
                                        <p>Allowed documents:</p>
                                        <ul>
                                            <li>Trade license (For Example FSSAI, Drug License, and License issued by Govt)</li>
                                            <li>UDYAM Certificate with annexure (4 Page)</li>
                                            <li>GST Certificate with annexure (3 Page)</li>
                                            <li>Shop and Establishment Certificate issued to operate business in a commercial space</li>
                                            <li>Current Account statement (Duration – Latest & Minimum 2 Years) showing business transactions and business name</li>
                                            <li>ITR Filing (Duration – Latest & Minimum 2 Years) showing business name and income from business</li>
                                        </ul>
                                    </div>
                                </form>
                            ) : (
                                <form>
                                    {["salary-slip1", "salary-slip2", "salary-slip3"].map((id, index) => (
                                        <>
                                            <div key={id} className="salary-slip-contain">
                                                <div className="main-container-doc-input">
                                                    <div>
                                                        <span>
                                                            <img src={Pdf_icon} alt="icon" />
                                                        </span>
                                                        <span>
                                                            <p>Salary Slip {index + 1}</p>
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="salary-slip-file">
                                                    <input
                                                        type="file"
                                                        id={id}
                                                        onChange={(e) => handleSalarySlipFileChange(e, id)}
                                                        accept=".pdf, .jpg, .jpeg, .png"
                                                    />
                                                    <div className="error">{salarySlipFileErrors[id]}</div>
                                                </div>
                                            </div>
                                            <FloatingInput
                                                type="text"
                                                label={`Enter Salary Slip ${index + 1} password (Mandatory for Password Protected Slips)`}
                                                value={salarySlipPasswords[id] || ""}
                                                on_change={(e) => handleSalarySlipPasswordChange(e, id)}
                                                class_name="docpass-input"
                                            />
                                        </>
                                    ))}
                                    {docError && <div className="error text-center">{docError}</div>}
                                    <div className="bottomDiv">
                                        <Button text="Proceed" type="submit" onClick={handleSalarySlipSubmit}
                                            disabled={
                                                !Object.values(salarySlipFiles).some(file => file) ||
                                                Object.values(salarySlipFileErrors).some(error => error)
                                            }
                                        />
                                    </div>
                                </form>
                            )
                        }
                    </DocUploadWrapper>
                </AfterLoginLayoutWrapper>
            )}
        </>
    );
};

export default DocUpload;
